import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import { roundTimeframe, findRoundUnit } from 'cio/utils/round-timeframe-units';
import pluralize from 'cio/utils/pluralize';

import MessageLimitModel from 'cio/models/message-limit';
import { MessageLimitProxy } from 'cio/pods/actions/messaging-limit/index/controller';
import { IdentifierKey, Identifiers, IdentifierState } from 'cio/utils/identifiers';

type DeliverySettings = 'normal' | 'send_to_one' | 'off';
type EditType = 'enable' | 'disable' | 'limit_change';
type MessageLimit = MessageLimitModel | MessageLimitProxy;

function currentIdentifier(identifiers: Identifiers): string {
  let identifierConfig: string;
  if (
    identifiers[IdentifierKey.CioId] === IdentifierState.EnabledImmutable &&
    identifiers[IdentifierKey.Email] === IdentifierState.Enabled
  ) {
    identifierConfig = 'multi-id';
  } else if (
    identifiers[IdentifierKey.CioId] === IdentifierState.Disabled &&
    identifiers[IdentifierKey.Email] === IdentifierState.Enabled
  ) {
    identifierConfig = 'email-based';
  } else {
    identifierConfig = 'id-based (Classic)';
  }
  return identifierConfig;
}

const Transforms: TransformsList = {
  createStarted(data) {
    return {
      Source: (
        {
          'setup-guide.sources': 'Setup Guide',
          'workspace-settings.edit': 'Identifier Tooltip',
          'settings.workspaces.index': 'Workspace List Page',
          dashboard: 'Email-based Intro Modal',
          'imports.import.setup.fields': 'Import CSV Map Fields Page',
        } as Record<string, string>
      )[data.previousRouteName],
    };
  },

  routeView(data: Data & { model: { deliverySettings: DeliverySettings } }) {
    return {
      'Workspace Delivery Settings': {
        normal: 'Normal',
        send_to_one: 'Test',
        off: 'Never',
      }[data.model!.deliverySettings],
    };
  },

  messageLimitsEditStarted(data: Data & { model: MessageLimit; editType: EditType }) {
    let timeframe = roundTimeframe(data.model.windowSecs);
    let unit = findRoundUnit(timeframe);
    let unitCount = Math.ceil(timeframe / unit.seconds);

    return {
      'Message Limit Edit Type': {
        enable: 'Enabled',
        disable: 'Disabled',
        limit_change: 'Limit Changed',
      }[data.editType],
      'Message Limit Count': data.model.count,
      'Message Limit Timeframe': `${unitCount} ${pluralize(unitCount, unit.value)}`,
    };
  },

  identifierClicked(data) {
    return {
      'Identifier Type': data.identifierType,
    };
  },

  identifierChangeStarted(data) {
    let identifierConfig: string = currentIdentifier(data.identifiers);

    return {
      'Current Identifier Configuration': identifierConfig,
    };
  },

  identifierChangeSubmitted(data) {
    return {
      'Current Identifier Configuration': currentIdentifier(data.identifiers),
      'Migrate to': data.migrateTo,
    };
  },

  updateIdentifiersById(data) {
    return {
      'Identify by ID': data.identifyById,
    };
  },

  updateDeliverySettings(data) {
    return {
      'Previous Delivery Settings': data.previousDeliverySettings,
      'Delivery Settings': data.deliverySettings,
    };
  },
};

export default Transforms;
